import React, { useState } from "react";
import { useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Grid,
  Paper,
  Fab,
  Icon,
  Stack,
  CircularProgress,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import PropTypes from "prop-types";
import MDButton from "components/MDButton";
import AddEmployeeDialog from "components/AddEmployeeDialog";
import MDBox from "components/MDBox";
import { getMyTeam, getIndividualForMyTeam } from "services/api";
import { useNavigate } from "react-router-dom";
import { useAuth } from "context/authContext";
import LoadingScreen from "components/Loading";
import { view } from "framer-motion";
import ListView from "./components/ListView";
import TeamChart from "./components/TeamPerformanceChart";
import MDTypography from "components/MDTypography";
import CommonKpiCharts from "./components/CommonKpis";
import { getScorecardExport } from "services/api";
import PerspectiveView from "./components/PerspectiveView";
import { useTranslation } from "react-i18next";
import CombinedCharts from "./components/CombinedCharts";
import { fieldStyles } from "services/utils";
import ExportButton from "components/UserExportButton";
import MonthYearSelector from "components/DateSelector";
import { buttonStyles } from "services/utils";
import { useAppTheme } from "context/themeContext";

const TeamView = () => {
  const { t, i18n } = useTranslation();

  const { primaryColor } = useAppTheme();
  const { employee, selectedMonth, selectedYear, quarter, isQuarterly } = useAuth();
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState(0);
  const [name, setName] = useState(employee.name);
  const [employeeHistory, setEmployeeHistory] = useState([employee.id]);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [myTeam, setMyTeam] = useState([]);
  const [myIndividualData, setMyData] = useState([]);
  useEffect(() => {
    const fetchMyTeam = async () => {
      setLoading(true);

      const myData = await getIndividualForMyTeam(
        !isQuarterly ? selectedMonth : quarter == 1 ? 3 : quarter == 2 ? 6 : quarter == 3 ? 9 : 12,
        selectedYear,
        employeeHistory[employeeHistory.length - 1],
        isQuarterly
      );

      if (myData !== "error") {
        if (myData.length > 0 && myData[0].length > 0) {
          setName(myData[0][1]);
          setMyData(myData);
        }
      }
      const myTeamData = await getMyTeam(
        !isQuarterly ? selectedMonth : quarter == 1 ? 3 : quarter == 2 ? 6 : quarter == 3 ? 9 : 12,
        selectedYear,
        employeeHistory[employeeHistory.length - 1],
        isQuarterly
      );
      console.log("MyTeamData", myTeamData);
      if (myTeamData !== "error") {
        setMyTeam(myTeamData);
      }
    };

    fetchMyTeam().then((e) => {
      setLoading(false);
    });
  }, [employeeHistory, selectedMonth, selectedYear, isQuarterly, quarter]);

  const handleEmployeeSelect = (newEmployee) => {
    setEmployeeHistory((prev) => [...prev, newEmployee]); // Store previous employee
  };

  const handleBack = () => {
    if (employeeHistory.length > 1) {
      setEmployeeHistory((prev) => prev.slice(0, -1)); // Remove last employee
    }
  };

  const handleDownload = async () => {
    setDownloadLoading(true);

    try {
      const isMonthly = !isQuarterly;
      const value = isMonthly ? selectedMonth : quarter;

      const downloadPromises = [];

      // Helper function to add download tasks
      const addDownloadTask = (employeeId, employeeName) => {
        if (employeeId && employeeId !== "null") {
          downloadPromises.push(
            getScorecardExport(employeeId, value, selectedYear, employeeName, isMonthly)
          );
        }
      };

      // Handle My Individual Data
      if (myIndividualData.length > 0) {
        let myInfo;
        if (Array.isArray(myIndividualData[0]) && myIndividualData.length > 0) {
          myInfo = myIndividualData[0];
        } else {
          myInfo = myIndividualData[0].split(",");
        }

        if (myInfo[0] && myInfo[0] !== "null" && myInfo.length > 1) {
          addDownloadTask(myInfo[0], myInfo[1].trim());
        }
      }

      // Handle Team Members
      myTeam.forEach((member) => {
        let employee;
        if (Array.isArray(member)) {
          employee = member;
        } else {
          employee = member.split(",");
        }

        if (employee[0] && employee[0] !== "null") {
          addDownloadTask(employee[0], employee[1]?.trim() || "Unknown");
        }
      });

      // Wait for all scorecard exports to complete
      await Promise.all(downloadPromises);

      // If you have performanceData for each team member, add evidence ZIP downloads
      // For now, this is commented out as you'd need the data structure for team members
      // const evidencePromises = myTeam.map(async (member) => {
      //   const employee = Array.isArray(member) ? member : member.split(",");
      //   if (employee[0] && employee[0] !== "null") {
      //     // You'd need to fetch performanceData for this employee first
      //     // const memberPerformanceData = await fetchPerformanceData(employee[0]);
      //     // if (memberPerformanceData && memberPerformanceData.length > 0) {
      //     //   return downloadAllEvidencesZip(
      //     //     memberPerformanceData,
      //     //     employee[1].trim(),
      //     //     value,
      //     //     selectedYear
      //     //   );
      //     // }
      //   }
      //   return Promise.resolve();
      // });
      // await Promise.all(evidencePromises);
    } catch (err) {
      console.error("Download failed for one or more team members", err);
      // Optionally show user feedback
    } finally {
      setDownloadLoading(false);
    }
  };

  return (
    <>
      {/* Button Group for Views */}
      <MDBox sx={{ mb: 3 }}>
        {/* Employee Name - Medium Size */}
        {view != 0 ? (
          <MDTypography variant="subtitle1" sx={{ fontWeight: 500, textAlign: "center", mb: 1 }}>
            {name}
          </MDTypography>
        ) : (
          ""
        )}
        <Stack
          direction={{ xs: "column", sm: "row" }} // Column on small screens, row on larger screens
          spacing={2}
        >
          {employeeHistory.length > 1 && (
            <Button
              startIcon={<Icon>arrow_back</Icon>}
              sx={{
                color: primaryColor,
                borderColor: primaryColor,
                "&:hover": { backgroundColor: primaryColor, color: "#fff" },
              }}
              onClick={handleBack}
            >
              {t("user.myTeam.back")}
            </Button>
          )}
          <Button
            sx={{ ...buttonStyles(view === 0), width: { xs: "100%", sm: "auto" } }}
            variant={view === 0 ? "contained" : "outlined"}
            onClick={() => setView(0)}
          >
            {t("user.myTeam.listView")}
          </Button>
          <Button
            sx={{ ...buttonStyles(view === 1), width: { xs: "100%", sm: "auto" } }}
            variant={view === 1 ? "contained" : "outlined"}
            onClick={() => setView(1)}
          >
            {t("user.myTeam.perspectiveView")}
          </Button>
          <Button
            sx={{ ...buttonStyles(view === 2), width: { xs: "100%", sm: "auto" } }}
            variant={view === 2 ? "contained" : "outlined"}
            onClick={() => setView(2)}
          >
            {t("user.myTeam.teamPerformance")}
          </Button>
        </Stack>
      </MDBox>
      <Box>
        {/* Filters Section */}
        {!loading && (
          <Grid
            item
            xs={12}
            sm={6}
            md={6}
            display="flex"
            justifyContent="flex-end"
            alignItems="center"
          >
            <ExportButton
              onClick={handleDownload}
              loading={downloadLoading}
              label={t("user.myTeam.exportTeam")} // or use any string like "Export Team"
            />
          </Grid>
        )}
      </Box>
      {loading ? <LoadingScreen /> : ""}

      {/* Conditionally Render List View */}
      {!loading && view === 0 ? (
        <ListView
          month={selectedMonth}
          year={selectedYear}
          myTeam={myTeam}
          myIndividualData={myIndividualData}
          setSelectedEmployee={handleEmployeeSelect}
        />
      ) : !loading && view == 1 ? (
        <PerspectiveView
          month={selectedMonth}
          year={selectedYear}
          employeeId={employeeHistory[employeeHistory.length - 1]}
          team={myTeam}
          individual={myIndividualData}
        />
      ) : (
        !loading && (
          <CombinedCharts
            month={selectedMonth}
            year={selectedYear}
            employee={employeeHistory[employeeHistory.length - 1]}
          />
        )
      )}
    </>
  );
};

export default TeamView;
